import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

type TemplateId = 'pv_ago' | 'statuts_sarl' | 'decision_gerant';

interface JuristeResult {
  template_id: TemplateId;
  societe_name: string;
  document_text: string;
  pages_estimated: number;
  legal_basis_count: number;
}

interface AssocieForm {
  nom: string;
  parts: string;
  apport_tnd: string; // statuts only
}

export function JuristeRnePage() {
  const [templateId, setTemplateId] = useState<TemplateId>('pv_ago');

  // Common fields
  const [societeName, setSocieteName] = useState('Sahel Textile Industries');
  const [matricule, setMatricule] = useState('1234567A/M/A/000');
  const [address, setAddress] = useState('15 rue de l\'Industrie, Sousse');
  const [capitalTnd, setCapitalTnd] = useState('250000.000');
  const [genDate, setGenDate] = useState('2026-05-04');

  // PV AGO
  const [fiscalYear, setFiscalYear] = useState(2025);
  const [resultatNetTnd, setResultatNetTnd] = useState('50000.000');
  const [affectation, setAffectation] = useState<'report_a_nouveau' | 'distribution_dividendes' | 'reserve_legale_partielle'>(
    'reserve_legale_partielle',
  );

  // Associes (PV AGO + Statuts)
  const [associes, setAssocies] = useState<AssocieForm[]>([
    { nom: 'Mohamed Ben Salem', parts: '600', apport_tnd: '150000.000' },
    { nom: 'Fatma Trabelsi', parts: '400', apport_tnd: '100000.000' },
  ]);

  // Statuts SARL
  const [objetSocial, setObjetSocial] = useState('Conseil en systèmes informatiques et services digitaux pour PME tunisiennes');
  const [duree, setDuree] = useState(99);
  const [gerantNom, setGerantNom] = useState('Mohamed Ben Salem');
  const [gerantQualite, setGerantQualite] = useState<'associé' | 'tiers'>('associé');

  // Décision gérant
  const [decisionObjet, setDecisionObjet] = useState('Approbation du budget et plan d\'investissement pour l\'exercice');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function updateAssocie(i: number, patch: Partial<AssocieForm>) {
    setAssocies((p) => p.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function addAssocie() {
    setAssocies((p) => [...p, { nom: '', parts: '100', apport_tnd: '10000.000' }]);
  }
  function removeAssocie(i: number) {
    setAssocies((p) => p.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const base = {
        societe_name: societeName,
        societe_matricule_fiscal: matricule,
        societe_address: address,
        capital_millimes: tndStringToMillimes(capitalTnd),
        generation_date: genDate,
      };
      let payload: Record<string, unknown>;
      if (templateId === 'pv_ago') {
        payload = {
          ...base,
          template_id: 'pv_ago',
          fiscal_year: fiscalYear,
          resultat_net_millimes: tndStringToMillimes(resultatNetTnd),
          affectation,
          associes: associes.map((a) => ({ nom: a.nom, parts: parseInt(a.parts, 10) })),
        };
      } else if (templateId === 'statuts_sarl') {
        payload = {
          ...base,
          template_id: 'statuts_sarl',
          objet_social: objetSocial,
          duree_annees: duree,
          gerant_nom: gerantNom,
          gerant_qualite: gerantQualite,
          associes: associes.map((a) => ({
            nom: a.nom,
            parts: parseInt(a.parts, 10),
            apport_millimes: tndStringToMillimes(a.apport_tnd),
          })),
        };
      } else {
        payload = {
          ...base,
          template_id: 'decision_gerant',
          fiscal_year: fiscalYear,
          decision_objet: decisionObjet,
          gerant_nom: gerantNom,
        };
      }
      const res = await api.invokeAgent('juriste_rne', { payload, fiscal_reference_date: genDate });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Juriste RNE"
      module="Extension · Phase 4"
      model="Sonnet 4.6"
      description={
        <>
          Génération d'actes juridiques tunisiens pré-remplis — PV d'AGO, statuts SARL, décision
          du gérant SUARL — avec citations du Code des obligations et des contrats + Code des
          sociétés commerciales.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Type d'acte">
          <div className="flex flex-wrap gap-2">
            {(['pv_ago', 'statuts_sarl', 'decision_gerant'] as TemplateId[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTemplateId(t)}
                className={`px-3 py-1.5 rounded border text-sm transition ${
                  templateId === t ? 'bg-ink text-cream border-ink' : 'bg-white text-ink border-border hover:border-gold'
                }`}
              >
                {labelTemplate(t)}
              </button>
            ))}
          </div>
        </FormCard>

        <FormCard title="Société">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Raison sociale"><input className={inputCls} value={societeName} onChange={(e) => setSocieteName(e.target.value)} required /></Field>
            <Field label="Matricule fiscal"><input className={inputCls + ' font-mono'} value={matricule} onChange={(e) => setMatricule(e.target.value.toUpperCase())} required /></Field>
            <Field label="Siège social"><input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} required /></Field>
            <Field label="Capital (TND)"><input className={inputCls + ' font-mono'} value={capitalTnd} onChange={(e) => setCapitalTnd(e.target.value)} required /></Field>
            <Field label="Date du document"><input type="date" className={inputCls} value={genDate} onChange={(e) => setGenDate(e.target.value)} required /></Field>
          </div>
        </FormCard>

        {templateId === 'pv_ago' && (
          <>
            <FormCard title="Résultat & affectation">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Exercice approuvé"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2025', 10))} /></Field>
                <Field label="Résultat net (TND)"><input className={inputCls + ' font-mono'} value={resultatNetTnd} onChange={(e) => setResultatNetTnd(e.target.value)} required /></Field>
                <Field label="Affectation">
                  <select className={inputCls} value={affectation} onChange={(e) => setAffectation(e.target.value as typeof affectation)}>
                    <option value="report_a_nouveau">Report à nouveau</option>
                    <option value="distribution_dividendes">Distribution dividendes</option>
                    <option value="reserve_legale_partielle">5% réserve légale + report</option>
                  </select>
                </Field>
              </div>
            </FormCard>
            <AssociesCard associes={associes} update={updateAssocie} add={addAssocie} remove={removeAssocie} showApport={false} />
          </>
        )}

        {templateId === 'statuts_sarl' && (
          <>
            <FormCard title="Objet social & durée">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Objet social" hint="Description précise de l'activité"><textarea rows={3} className={inputCls} value={objetSocial} onChange={(e) => setObjetSocial(e.target.value)} required /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Durée (années)"><input type="number" className={inputCls} value={duree} onChange={(e) => setDuree(parseInt(e.target.value || '99', 10))} min={1} max={99} /></Field>
                  <Field label="Qualité gérant">
                    <select className={inputCls} value={gerantQualite} onChange={(e) => setGerantQualite(e.target.value as typeof gerantQualite)}>
                      <option value="associé">Associé</option>
                      <option value="tiers">Tiers</option>
                    </select>
                  </Field>
                  <Field label="Nom gérant"><input className={inputCls} value={gerantNom} onChange={(e) => setGerantNom(e.target.value)} required /></Field>
                </div>
              </div>
            </FormCard>
            <AssociesCard associes={associes} update={updateAssocie} add={addAssocie} remove={removeAssocie} showApport={true} />
          </>
        )}

        {templateId === 'decision_gerant' && (
          <FormCard title="Décision">
            <div className="grid grid-cols-1 gap-3">
              <Field label="Exercice"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
              <Field label="Nom gérant"><input className={inputCls} value={gerantNom} onChange={(e) => setGerantNom(e.target.value)} required /></Field>
              <Field label="Objet de la décision" hint="Décrire avec précision (10 caractères min)"><textarea rows={3} className={inputCls} value={decisionObjet} onChange={(e) => setDecisionObjet(e.target.value)} required /></Field>
            </div>
          </FormCard>
        )}

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Génération…' : 'Générer le document'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <JuristeResultView result={result as JuristeResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function AssociesCard({
  associes,
  update,
  add,
  remove,
  showApport,
}: {
  associes: AssocieForm[];
  update: (i: number, patch: Partial<AssocieForm>) => void;
  add: () => void;
  remove: (i: number) => void;
  showApport: boolean;
}) {
  const totalParts = associes.reduce((s, a) => s + (parseInt(a.parts, 10) || 0), 0);
  return (
    <FormCard
      title={`Associés (${associes.length}) — total ${totalParts} parts`}
      action={<button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">+ Ajouter</button>}
    >
      <div className="space-y-2">
        {associes.map((a, idx) => (
          <div key={idx} className="border border-border rounded p-3 bg-cream/30 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
            <Field label="Nom"><input className={inputCls} value={a.nom} onChange={(e) => update(idx, { nom: e.target.value })} required /></Field>
            <Field label="Parts"><input className={inputCls + ' font-mono'} value={a.parts} onChange={(e) => update(idx, { parts: e.target.value })} required /></Field>
            {showApport && <Field label="Apport (TND)"><input className={inputCls + ' font-mono'} value={a.apport_tnd} onChange={(e) => update(idx, { apport_tnd: e.target.value })} required /></Field>}
            {associes.length > 1 && <button type="button" onClick={() => remove(idx)} className="text-xs text-red-700 hover:text-red-900 self-end">Supprimer</button>}
          </div>
        ))}
      </div>
    </FormCard>
  );
}

function JuristeResultView({ result }: { result: JuristeResult }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="px-2 py-1 rounded bg-ink text-cream text-xs uppercase tracking-wider">{labelTemplate(result.template_id)}</span>
        <span className="text-sm">Société <strong>{result.societe_name}</strong></span>
        <span className="text-xs text-taupe">~{result.pages_estimated} page(s) · {result.legal_basis_count} citation(s) légale(s)</span>
      </div>
      <div className="text-xs uppercase tracking-wider text-taupe">Document généré (à signer)</div>
      <pre className="font-serif text-sm whitespace-pre-wrap bg-white border border-border rounded-lg p-6 leading-relaxed shadow-sm max-h-[600px] overflow-auto">
        {result.document_text}
      </pre>
      <div className="text-xs text-taupe italic">
        Document bootstrap — relecture par avocat tunisien recommandée avant signature et dépôt RNE.
      </div>
    </div>
  );
}

function labelTemplate(t: TemplateId): string {
  return ({ pv_ago: 'PV d\'AGO', statuts_sarl: 'Statuts SARL', decision_gerant: 'Décision du gérant' } as Record<TemplateId, string>)[t];
}
